// 〈{leaf}〉› 用電設定（/sites/energy/:homeId；plan SPEC NRG-V7，設計稿 v05 / v05b；家戶 owner / admin 可改）：
//   一頁兩段——「電價」（沿用組織方案 / 改選 / 自訂）、「用電提醒」（PoE 供電超過 ___% 注意 / 異常；讀不到資料超過 ___ 小時）；每列各自儲存（UX-06）；
//   沒權限 → 目前值 + 鎖頭 +「只有擁有者或管理員能修改」；家戶開了 IT 監控時 PoE 門檻下方說明會開事故，否則只進收件匣；
//   範本管理中的值唯讀標「由範本〈名稱〉管理」+〔解除範本管理〕。
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useScope } from '../../lib/scope';
import { useConfirm } from '../../components/ConfirmDialog';
import { energyApi, energyMode } from './api';
import { useEnergyEnv } from './hooks';
import { energyErrText, LoadError, MockBanner, SetRow } from './parts';
import type { EnergySettings } from './types';
import './energy.css';

export function LeafEnergySettingsPage() {
  const { t } = useTranslation();
  useEnergyEnv();
  const { homeId = '' } = useParams();
  const { homes, orgs, booting } = useScope();
  const home = homes.find((h) => h.home_id === homeId && !h.external);
  const api = home?.endpoints?.api ?? orgs.find((o) => o.org_id === home?.org_id)?.api ?? '';
  const key = ['energy', 'settings', energyMode(), homeId];
  const q = useQuery({ queryKey: key, queryFn: () => energyApi.settings(api, homeId), enabled: !!home, retry: false });
  if (!booting && !home)
    return (
      <div className="page en-root">
        <h1 className="page-title">{t('energy:leaf.title')}</h1>
        <p className="muted">{t('energy:leaf.not_found')}</p>
      </div>
    );
  const name = home?.display_name ?? homeId;
  return (
    <div className="page en-root km">
      <div className="en-page">
        <nav className="small muted" aria-label="breadcrumb">
          <Link to="/sites">{t('energy:leaf.crumb_sites')}</Link> › {name}
        </nav>
        <div className="en-head">
          <h1 className="page-title">
            {name} · {t('energy:leaf.title')}
          </h1>
          <span className="spacer" />
          <Link to="/energy" className="btn btn-sm">
            {t('energy:leaf.to_overview')}
          </Link>
        </div>
        <MockBanner />
        {q.isError ? (
          <LoadError error={q.error} retry={() => void q.refetch()} />
        ) : !q.data ? (
          <section className="card" aria-busy="true">
            <div className="skel" />
            <div className="skel" />
          </section>
        ) : (
          <SettingsBody s={q.data} api={api} homeId={homeId} orgId={home?.org_id ?? ''} role={home?.role ?? ''} />
        )}
      </div>
    </div>
  );
}

function SettingsBody({ s, api, homeId, orgId, role }: { s: EnergySettings; api: string; homeId: string; orgId: string; role: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { orgs } = useScope();
  const { ask, dialog } = useConfirm();
  const key = ['energy', 'settings', energyMode(), homeId];
  const canEdit = s.can_edit ?? (role === 'owner' || role === 'admin');
  const managed = !!s.managed_by_template;
  const orgApi = orgs.find((o) => o.org_id === orgId)?.api ?? api;
  const tq = useQuery({ queryKey: ['energy', 'tariffs', energyMode(), orgId], queryFn: () => energyApi.tariffs(orgApi, orgId), enabled: canEdit && !!orgId, retry: false });
  const [saved, setSaved] = useState<string | null>(null);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const m = useMutation({
    mutationFn: (body: Partial<EnergySettings>) => energyApi.putSettings(api, homeId, { ...body, version: (qc.getQueryData(key) as EnergySettings | undefined)?.version }),
    onSuccess: (d, body) => {
      qc.setQueryData(key, d);
      setSaved(Object.keys(body.alerts ?? body)[0]);
      void qc.invalidateQueries({ queryKey: ['energy', 'summary'] });
      void qc.invalidateQueries({ queryKey: ['energy', 'tariffs'] });
    },
  });
  const [warn, setWarn] = useState(String(s.alerts.poe_warn_pct));
  const [high, setHigh] = useState(String(s.alerts.poe_high_pct));
  const [gap, setGap] = useState(String(s.alerts.gap_hours));
  useEffect(() => {
    setWarn(String(s.alerts.poe_warn_pct));
    setHigh(String(s.alerts.poe_high_pct));
    setGap(String(s.alerts.gap_hours));
  }, [s.alerts.poe_warn_pct, s.alerts.poe_high_pct, s.alerts.gap_hours]);
  const check = (field: string, v: string, min: number, max: number): number | null => {
    const n = Number(v);
    if (!/^\d+$/.test(v.trim()) || n < min || n > max) {
      setErrs((e) => ({ ...e, [field]: t('energy:leaf.err_range', { min, max }) }));
      return null;
    }
    setErrs((e) => ({ ...e, [field]: '' }));
    return n;
  };
  const saveAlert = (field: 'poe_warn_pct' | 'poe_high_pct' | 'gap_hours', v: string) => {
    // PoE 門檻 50–100、注意 ≤ 異常（同 kmgr 監控範本 power 項的檢查，internal/template/body.go）
    const n = field === 'gap_hours' ? check(field, v, 1, 72) : check(field, v, 50, 100);
    if (n === null || n === s.alerts[field]) return;
    const next = { ...s.alerts, [field]: n };
    if (next.poe_warn_pct > next.poe_high_pct) return setErrs((e) => ({ ...e, [field]: t('energy:leaf.err_order') }));
    m.mutate({ alerts: next });
  };
  const unmanage = async () => {
    if (!(await ask({ title: t('energy:leaf.unmanage'), body: t('energy:leaf.unmanage_confirm'), action: t('energy:leaf.unmanage'), danger: true }))) return;
    m.mutate({ managed_by_template: null });
  };
  const tariffs = (tq.data?.items ?? []).filter((x) => x.org_id === orgId);
  const lock = (
    <span className="en-lock-inline" aria-label={t('energy:leaf.locked')}>
      🔒
    </span>
  );
  const ok = (f: string) => (saved === f && !m.isPending ? <span className="ok">{t('energy:leaf.saved')}</span> : m.isPending && saved === null ? <span className="small muted">{t('energy:leaf.saving')}</span> : null);
  const tariffText = s.tariff_source === 'org' ? t('energy:leaf.tariff_org', { name: s.tariff_name ?? '' }) : s.tariff_source === 'home' ? t('energy:leaf.tariff_home', { name: s.tariff_name ?? '' }) : t('energy:leaf.tariff_none');
  const pctInput = (field: 'poe_warn_pct' | 'poe_high_pct', v: string, set: (x: string) => void, label: string) =>
    !canEdit || managed ? (
      <span className="en-ro">
        {label} {s.alerts[field]}%
      </span>
    ) : (
      <label className="en-row" style={{ gap: 6 }}>
        {label}
        <input
          className="input en-pct"
          inputMode="numeric"
          value={v}
          onChange={(e) => set(e.target.value)}
          onBlur={() => saveAlert(field, v)}
          onKeyDown={(e) => e.key === 'Enter' && saveAlert(field, v)}
          aria-invalid={!!errs[field]}
          style={{ textAlign: 'right' }}
          data-testid={`en-${field}`}
        />
        %
      </label>
    );
  return (
    <>
      {!canEdit && (
        <p className="en-lock" data-testid="en-locked">
          🔒 {t('energy:leaf.locked')}
        </p>
      )}
      <section className="card en-section" data-testid="en-leaf-tariff">
        <h2 className="section-title">{t('energy:leaf.tariff')}</h2>
        <SetRow label={t('energy:leaf.tariff')} act={ok('tariff_id')}>
          {canEdit ? (
            <>
              <div className="small">{tariffText}</div>
              <select
                className="input"
                aria-label={t('energy:leaf.tariff_pick')}
                value={s.tariff_id ?? ''}
                onChange={(e) => {
                  setSaved(null);
                  m.mutate({ tariff_id: e.target.value || null });
                }}
                data-testid="en-leaf-tariff-select"
              >
                <option value="">{t('energy:leaf.tariff_none_opt')}</option>
                {tariffs.map((x) => (
                  <option key={x.tariff_id} value={x.tariff_id}>
                    {x.name}（{x.currency}）
                  </option>
                ))}
              </select>
              <div className="en-note">
                <Link to="/org/energy">{t('energy:leaf.tariff_custom')}</Link>
              </div>
            </>
          ) : (
            <span>
              {tariffText}
              {lock}
            </span>
          )}
        </SetRow>
      </section>
      <section className="card en-section" data-testid="en-leaf-alerts">
        <div className="en-row">
          <h2 className="section-title" style={{ margin: 0 }}>
            {t('energy:leaf.alerts')}
          </h2>
          {managed && (
            <span className="km-st" data-testid="en-managed">
              🔒 {t('energy:leaf.managed', { name: s.managed_by_template })}
            </span>
          )}
          <span className="spacer" />
          {managed && canEdit && (
            <button type="button" className="btn btn-sm" onClick={() => void unmanage()} data-testid="en-unmanage">
              {t('energy:leaf.unmanage')}
            </button>
          )}
        </div>
        <SetRow label={t('energy:leaf.poe')} desc={t('energy:leaf.poe_desc')} act={ok('poe_warn_pct') ?? ok('poe_high_pct')}>
          <div className="en-row">
            {pctInput('poe_warn_pct', warn, setWarn, t('energy:leaf.poe_warn'))}
            {pctInput('poe_high_pct', high, setHigh, t('energy:leaf.poe_high'))}
            {!canEdit && !managed && lock}
          </div>
          {(errs.poe_warn_pct || errs.poe_high_pct) && (
            <p className="en-err small" role="alert">
              ⚠ {errs.poe_warn_pct || errs.poe_high_pct}
            </p>
          )}
          <p className="en-note" data-testid="en-kmgr-line">
            {s.kmgr_enabled ? t('energy:leaf.kmgr_on') : t('energy:leaf.kmgr_off')}
          </p>
        </SetRow>
        <SetRow label={t('energy:leaf.gap')} desc={t('energy:leaf.gap_desc')} act={ok('gap_hours')}>
          {canEdit ? (
            <label className="en-row" style={{ gap: 6 }}>
              <input
                className="input en-pct"
                inputMode="numeric"
                value={gap}
                onChange={(e) => setGap(e.target.value)}
                onBlur={() => saveAlert('gap_hours', gap)}
                onKeyDown={(e) => e.key === 'Enter' && saveAlert('gap_hours', gap)}
                aria-label={t('energy:leaf.gap')}
                aria-invalid={!!errs.gap_hours}
                style={{ textAlign: 'right' }}
                data-testid="en-gap_hours"
              />
              {t('energy:leaf.hours')}
            </label>
          ) : (
            <span className="en-ro">
              {s.alerts.gap_hours} {t('energy:leaf.hours')}
              {lock}
            </span>
          )}
          {errs.gap_hours && (
            <p className="en-err small" role="alert">
              ⚠ {errs.gap_hours}
            </p>
          )}
        </SetRow>
        {m.isError && (
          <p className="en-err" role="alert">
            {energyErrText(t, m.error)}
          </p>
        )}
      </section>
      {dialog}
    </>
  );
}
